import { FC, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoBrandFilterAtom,
  giftItemInfoDeliveryCategoryFilterAtom,
  giftItemInfoListDataAtom,
  giftItemInfoListStatusAtom,
  giftItemInfoSearchQueryAtom,
  GiftItemInfoSortType,
  giftItemInfoSortTypeAtom,
  GiftItemInfoStatusFilter,
  giftItemInfoStatusFilterAtom,
  giftItemInfoSubCategoryFilterAtom,
  GiftItemInfoTaxFilter,
  giftItemInfoTaxFilterAtom,
  GiftItemInfoTypeFilter,
  giftItemInfoTypeFilterAtom,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { bulkSetGiftItemInfoEnabled } from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon } from '@heroicons/react/outline';

import styles from '../components/GiftBrandManagement.module.css';
import { GiftItemInfoRow } from '../components/GiftItemInfoRow';

const inputClass =
  'w-full min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:bg-gray-50';

export const GiftItemInfoListPage: FC = function GiftItemInfoListPage() {
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();

  useGiftItemInfoListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftDeliveryCategoryListHook(sellerRole());

  const status = useRecoilValue(giftItemInfoListStatusAtom);
  const data = useRecoilValue(giftItemInfoListDataAtom);

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 배송 카테고리 목록
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 필터 및 검색 상태 (Recoil로 관리하여 페이지 이동 후에도 유지)
  const [searchQuery, setSearchQuery] = useRecoilState(giftItemInfoSearchQueryAtom);
  const [typeFilter, setTypeFilter] = useRecoilState(giftItemInfoTypeFilterAtom);
  const [statusFilter, setStatusFilter] = useRecoilState(giftItemInfoStatusFilterAtom);
  const [brandFilter, setBrandFilter] = useRecoilState(giftItemInfoBrandFilterAtom);
  const [deliveryCategoryFilter, setDeliveryCategoryFilter] = useRecoilState(
    giftItemInfoDeliveryCategoryFilterAtom,
  );
  const [subCategoryFilter, setSubCategoryFilter] = useRecoilState(
    giftItemInfoSubCategoryFilterAtom,
  );
  const [taxFilter, setTaxFilter] = useRecoilState(giftItemInfoTaxFilterAtom);
  const [sortType, setSortType] = useRecoilState(giftItemInfoSortTypeAtom);

  // 배송 카테고리 필터는 배송형에서만 사용하므로,
  // 배송형이 아니면 배송 카테고리 필터를 초기화한다.
  const handleTypeFilterChange = (value: GiftItemInfoTypeFilter) => {
    setTypeFilter(value);
    if (value !== 'Delivery') {
      setDeliveryCategoryFilter('all');
      setSubCategoryFilter('all');
    }
  };

  // 배송 카테고리가 바뀌면 하위분류 필터는 초기화 (카테고리마다 하위분류가 다름)
  const handleDeliveryCategoryFilterChange = (value: string) => {
    setDeliveryCategoryFilter(value);
    setSubCategoryFilter('all');
  };

  // 필터링 및 정렬된 데이터
  const filteredAndSortedData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 1. 검색 필터 (이름, 태그, 상품코드)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
          (item.productCode != null && String(item.productCode).includes(query)),
      );
    }

    // 2. 타입 필터
    if (typeFilter !== 'all') {
      result = result.filter((item) => item.type === typeFilter);
    }

    // 3. 활성화 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) =>
        statusFilter === 'enabled' ? item.isEnabled : !item.isEnabled,
      );
    }

    // 4. 과세여부 필터
    if (taxFilter !== 'all') {
      result = result.filter((item) =>
        taxFilter === 'taxable' ? item.isTaxable !== false : item.isTaxable === false,
      );
    }

    // 5. 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        // 브랜드 없는 상품
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    // 6. 배송 카테고리 필터 (배송형 상품에만 적용)
    if (deliveryCategoryFilter !== 'all') {
      if (deliveryCategoryFilter === 'none') {
        // 배송 카테고리가 지정되지 않은 상품
        result = result.filter(
          (item) => item.type === GiftItemInfoType.Delivery && !item.giftDeliveryCategory,
        );
      } else {
        result = result.filter((item) => {
          if (item.type !== GiftItemInfoType.Delivery) return false;
          const categoryId =
            typeof item.giftDeliveryCategory === 'object'
              ? item.giftDeliveryCategory?._id
              : item.giftDeliveryCategory;
          return categoryId === deliveryCategoryFilter;
        });
      }
    }

    // 6-1. 하위분류 필터 (배송형 상품에만 적용)
    if (subCategoryFilter !== 'all') {
      if (subCategoryFilter === 'none') {
        result = result.filter(
          (item) => item.type === GiftItemInfoType.Delivery && !item.subCategory,
        );
      } else {
        result = result.filter(
          (item) =>
            item.type === GiftItemInfoType.Delivery &&
            item.subCategory === subCategoryFilter,
        );
      }
    }

    // 7. 정렬
    switch (sortType) {
      case 'order':
        result.sort((a, b) => {
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name, 'ko'));
        break;
      case 'price-asc':
        result.sort((a, b) => (a.priceWhole || 0) - (b.priceWhole || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceWhole || 0) - (a.priceWhole || 0));
        break;
      case 'latest':
        result.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case 'oldest':
        result.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
    }

    return result;
  }, [
    data,
    searchQuery,
    typeFilter,
    statusFilter,
    taxFilter,
    brandFilter,
    deliveryCategoryFilter,
    subCategoryFilter,
    sortType,
  ]);

  // 마진율(%) 계산: (판매가 - 도매가) / 판매가 * 100
  const getMarginRate = (item: GiftItemInfoData): number | null => {
    if (!item.priceRetail || !item.priceWhole) return null;
    return ((item.priceRetail - item.priceWhole) / item.priceRetail) * 100;
  };

  // 같은 하위분류 중 마진율이 가장 높은 상품의 _id 집합
  // (배송 카테고리 + 하위분류명으로 그룹화)
  const topMarginIdSet = useMemo(() => {
    const set = new Set<string>();
    if (!data) return set;

    const bestByGroup = new Map<string, { id: string; rate: number }>();
    for (const item of data) {
      if (!item.subCategory) continue;
      const rate = getMarginRate(item);
      if (rate === null) continue;
      const categoryId =
        typeof item.giftDeliveryCategory === 'object'
          ? item.giftDeliveryCategory?._id
          : item.giftDeliveryCategory;
      const groupKey = `${categoryId ?? 'none'}::${item.subCategory}`;
      const best = bestByGroup.get(groupKey);
      if (!best || rate > best.rate) {
        bestByGroup.set(groupKey, { id: item._id, rate });
      }
    }
    Array.from(bestByGroup.values()).forEach(({ id }) => set.add(id));
    return set;
  }, [data]);

  // 하위분류 필터 옵션 목록
  // 특정 배송 카테고리가 선택되면 해당 카테고리의 subCategories 기준,
  // 그렇지 않으면 전체 배송형 상품에서 사용 중인 하위분류를 수집한다.
  const availableSubCategories = useMemo(() => {
    if (deliveryCategoryFilter !== 'all' && deliveryCategoryFilter !== 'none') {
      const category = deliveryCategoryList.find((c) => c._id === deliveryCategoryFilter);
      if (category?.subCategories?.length) {
        return [...category.subCategories];
      }
    }
    if (!data) return [];
    const set = new Set<string>();
    for (const item of data) {
      if (item.type === GiftItemInfoType.Delivery && item.subCategory) {
        set.add(item.subCategory);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [data, deliveryCategoryFilter, deliveryCategoryList]);

  // 통계 정보
  const stats = useMemo(() => {
    if (!data) return { total: 0, delivery: 0, coupon: 0, enabled: 0, disabled: 0 };
    return {
      total: data.length,
      delivery: data.filter((item) => item.type === GiftItemInfoType.Delivery).length,
      coupon: data.filter((item) => item.type === GiftItemInfoType.Coupon).length,
      enabled: data.filter((item) => item.isEnabled).length,
      disabled: data.filter((item) => !item.isEnabled).length,
    };
  }, [data]);

  // 브랜드 일괄 활성/비활성 — 특정 브랜드를 골랐을 때만 노출, 현재 필터 결과(표시 중인 목록)에 적용
  const [bulkEnabling, setBulkEnabling] = useState(false);
  const selectedBrandName = useMemo(
    () => brandList.find((b) => b._id === brandFilter)?.name,
    [brandList, brandFilter],
  );
  const bulkTarget = useMemo(() => {
    if (brandFilter === 'all' || brandFilter === 'none') return null;
    const enabled = filteredAndSortedData.filter((item) => item.isEnabled);
    const disabled = filteredAndSortedData.filter((item) => !item.isEnabled);
    return { enabled, disabled };
  }, [brandFilter, filteredAndSortedData]);

  const handleBulkEnabled = async (isEnabled: boolean) => {
    if (!bulkTarget) return;
    const targets = isEnabled ? bulkTarget.disabled : bulkTarget.enabled;
    if (targets.length === 0) return;
    const label = isEnabled ? '활성' : '비활성';
    if (
      !confirm(
        `${selectedBrandName ?? '선택 브랜드'} 원상품 ${
          targets.length
        }개를 ${label}으로 바꿉니다.\n(현재 표시 중인 목록 기준)`,
      )
    ) {
      return;
    }
    setBulkEnabling(true);
    try {
      await bulkSetGiftItemInfoEnabled(
        targets.map((item) => item._id),
        isEnabled,
        sellerRole(),
      );
      await invalidateQueriesByUrlPart(queryClient, 'gift-item-info');
    } catch (error) {
      console.error(error);
      alert('일괄 변경에 실패했습니다.');
    } finally {
      setBulkEnabling(false);
    }
  };

  const hasActiveFilter =
    Boolean(searchQuery) ||
    typeFilter !== 'all' ||
    statusFilter !== 'all' ||
    taxFilter !== 'all' ||
    brandFilter !== 'all' ||
    deliveryCategoryFilter !== 'all' ||
    subCategoryFilter !== 'all' ||
    sortType !== 'order';
  const resetFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setStatusFilter('all');
    setBrandFilter('all');
    setDeliveryCategoryFilter('all');
    setSubCategoryFilter('all');
    setTaxFilter('all');
    setSortType('order');
  };
  const brandNames = useMemo(
    () => new Map(brandList.map((brand) => [brand._id, brand.name])),
    [brandList],
  );
  const categoryNames = useMemo(
    () => new Map(deliveryCategoryList.map((category) => [category._id, category.name])),
    [deliveryCategoryList],
  );

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      {bulkEnabling && (
        <div
          role="status"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80"
        >
          <Loading />
          <p className="mt-3 text-sm text-gray-700">상태 변경 중…</p>
        </div>
      )}
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">원상품 관리</h1>
            <p className="mt-2 text-sm text-gray-500">
              원상품의 가격과 판매 상태, 브랜드·카테고리·선택유닛 연결을 관리합니다.
            </p>
          </div>
          <Link
            to="/gift-seller/item-info/new"
            className="inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <PlusIcon aria-hidden className="h-4 w-4" />
            원상품 등록
          </Link>
        </header>
        <section
          aria-label="원상품 목록"
          className="rounded-md border border-gray-200 bg-white"
        >
          <div className="space-y-4 px-5 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2" aria-label="상품 유형 필터">
                {(
                  [
                    ['all', '전체', stats.total],
                    ['Delivery', '배송형', stats.delivery],
                    ['Coupon', '쿠폰형', stats.coupon],
                  ] as const
                ).map(([value, label, count]) => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={typeFilter === value}
                    onClick={() => handleTypeFilterChange(value)}
                    className={`rounded px-3 py-2 text-sm ${
                      typeFilter === value
                        ? 'bg-gray-900 font-medium text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {label}{' '}
                    <span className="ml-1 text-xs">
                      {status === 'success' ? count : '—'}
                    </span>
                  </button>
                ))}
              </div>
              <div className="relative w-full lg:max-w-sm">
                <SearchIcon
                  aria-hidden
                  className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                />
                <input
                  type="search"
                  aria-label="원상품 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="상품명, 태그, 상품코드 검색"
                  className={`${inputClass} pl-9`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <label className="space-y-1 text-xs text-gray-500">
                <span>브랜드</span>
                <select
                  aria-label="브랜드"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className={inputClass}
                  disabled={brandListStatus !== 'success'}
                >
                  <option value="all">전체 브랜드</option>
                  <option value="none">브랜드 없음</option>
                  {brandFilter !== 'all' &&
                    brandFilter !== 'none' &&
                    !brandNames.has(brandFilter) && (
                      <option value={brandFilter}>선택 브랜드</option>
                    )}
                  {brandList.map((brand) => (
                    <option key={brand._id} value={brand._id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs text-gray-500">
                <span>판매 상태</span>
                <select
                  aria-label="판매 상태"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as GiftItemInfoStatusFilter)
                  }
                  className={inputClass}
                >
                  <option value="all">전체 상태</option>
                  <option value="enabled">활성</option>
                  <option value="disabled">비활성</option>
                </select>
              </label>
              <label className="space-y-1 text-xs text-gray-500">
                <span>과세 여부</span>
                <select
                  aria-label="과세 여부"
                  value={taxFilter}
                  onChange={(e) => setTaxFilter(e.target.value as GiftItemInfoTaxFilter)}
                  className={inputClass}
                >
                  <option value="all">전체 과세</option>
                  <option value="taxable">과세</option>
                  <option value="taxFree">비과세</option>
                </select>
              </label>
              <label className="space-y-1 text-xs text-gray-500">
                <span>정렬</span>
                <select
                  aria-label="정렬"
                  value={sortType}
                  onChange={(e) => setSortType(e.target.value as GiftItemInfoSortType)}
                  className={inputClass}
                >
                  <option value="order">표시순서</option>
                  <option value="latest">최신순</option>
                  <option value="oldest">오래된순</option>
                  <option value="name-asc">이름 ㄱ→ㅎ</option>
                  <option value="name-desc">이름 ㅎ→ㄱ</option>
                  <option value="price-asc">도매가 낮은순</option>
                  <option value="price-desc">도매가 높은순</option>
                </select>
              </label>
              {typeFilter === 'Delivery' && (
                <>
                  <label className="space-y-1 text-xs text-gray-500">
                    <span>배송 카테고리</span>
                    <select
                      aria-label="배송 카테고리"
                      value={deliveryCategoryFilter}
                      onChange={(e) => handleDeliveryCategoryFilterChange(e.target.value)}
                      className={inputClass}
                      disabled={deliveryCategoryListStatus !== 'success'}
                    >
                      <option value="all">전체 배송 카테고리</option>
                      <option value="none">카테고리 없음</option>
                      {deliveryCategoryFilter !== 'all' &&
                        deliveryCategoryFilter !== 'none' &&
                        !categoryNames.has(deliveryCategoryFilter) && (
                          <option value={deliveryCategoryFilter}>선택 카테고리</option>
                        )}
                      {deliveryCategoryList.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="space-y-1 text-xs text-gray-500">
                    <span>하위분류</span>
                    <select
                      aria-label="하위분류"
                      value={subCategoryFilter}
                      onChange={(e) => setSubCategoryFilter(e.target.value)}
                      className={inputClass}
                    >
                      <option value="all">전체 하위분류</option>
                      <option value="none">하위분류 없음</option>
                      {availableSubCategories.map((sub) => (
                        <option key={sub} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </div>
            {brandListStatus === 'error' && (
              <p role="alert" className="text-sm text-red-600">
                브랜드 목록을 불러오지 못했습니다.{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() =>
                    void invalidateQueriesByUrlPart(queryClient, 'gift-item-brand')
                  }
                >
                  브랜드 다시 조회
                </button>
              </p>
            )}
            {typeFilter === 'Delivery' && deliveryCategoryListStatus === 'error' && (
              <p role="alert" className="text-sm text-red-600">
                배송 카테고리를 불러오지 못했습니다.{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() =>
                    void invalidateQueriesByUrlPart(queryClient, 'gift-delivery-category')
                  }
                >
                  카테고리 다시 조회
                </button>
              </p>
            )}
          </div>
          {status === 'success' && bulkTarget && (
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 bg-gray-50 px-5 py-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">
                  {selectedBrandName ?? '선택 브랜드'} 일괄 변경
                </span>
                <p className="mt-1 text-xs text-gray-500">
                  현재 검색·필터에 해당하는 {filteredAndSortedData.length}개 상품 기준
                </p>
              </div>
              <div className="flex flex-wrap gap-2 xl:ml-auto">
                <button
                  type="button"
                  onClick={() => void handleBulkEnabled(true)}
                  disabled={bulkEnabling || bulkTarget.disabled.length === 0}
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                >
                  비활성 {bulkTarget.disabled.length}개 → 활성
                </button>
                <button
                  type="button"
                  onClick={() => void handleBulkEnabled(false)}
                  disabled={bulkEnabling || bulkTarget.enabled.length === 0}
                  className="rounded border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40"
                >
                  활성 {bulkTarget.enabled.length}개 → 비활성
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
            <span aria-live="polite">
              {status === 'success'
                ? `전체 ${stats.total}개 중 ${filteredAndSortedData.length}개 표시`
                : '원상품 목록'}
            </span>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100"
              >
                필터 초기화
              </button>
            )}
          </div>
          {status === 'error' ? (
            <div role="alert" className="border-t border-gray-200 px-5 py-12 text-center">
              <p className="text-sm text-gray-600">원상품 목록을 불러오지 못했습니다.</p>
              <button
                type="button"
                onClick={() =>
                  void invalidateQueriesByUrlPart(queryClient, 'gift-item-info')
                }
                className="mt-4 rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                다시 시도
              </button>
            </div>
          ) : status !== 'success' ? (
            <div
              role="status"
              aria-label="원상품 불러오는 중"
              className="flex justify-center py-16"
            >
              <Loading />
            </div>
          ) : filteredAndSortedData.length === 0 ? (
            <div className="border-t border-gray-200 px-5 py-12 text-center">
              <p className="text-sm text-gray-600">
                {hasActiveFilter
                  ? '조건에 맞는 원상품이 없습니다. 검색어나 필터를 변경해주세요.'
                  : '등록된 원상품이 없습니다.'}
              </p>
              {!hasActiveFilter && (
                <Link
                  to="/gift-seller/item-info/new"
                  className="mt-4 inline-block text-sm text-indigo-700 underline"
                >
                  첫 원상품 등록
                </Link>
              )}
            </div>
          ) : (
            <table className="block w-full table-fixed text-left xl:table">
              <caption className="sr-only">원상품별 연결 정보, 가격 및 판매 상태</caption>
              <thead className="hidden bg-gray-50 text-xs text-gray-500 xl:table-header-group">
                <tr>
                  {['원상품', '브랜드 · 카테고리', '가격', '유형 · 상태', '관리'].map(
                    (title, index) => (
                      <th
                        key={title}
                        className={`px-5 py-3 font-medium ${
                          index === 0
                            ? 'w-[28%]'
                            : index === 2
                            ? 'w-[23%]'
                            : index === 4
                            ? 'w-[15%] text-right'
                            : ''
                        }`}
                      >
                        {title}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="block xl:table-row-group">
                {filteredAndSortedData.map((item) => (
                  <GiftItemInfoRow
                    key={item._id}
                    item={item}
                    topMargin={topMarginIdSet.has(item._id)}
                    brandNames={brandNames}
                    categoryNames={categoryNames}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
};
